"use client";

import { useApolloClient, useMutation, useSuspenseQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input, Stack } from "@mantine/core";
import { formatISO } from "date-fns";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Button,
  Center,
  Divider,
  Group,
  H2,
  Paper,
  Text,
} from "@/components/ui/elements";
import {
  DatePickerInputField,
  InputField,
  RadioField,
} from "@/components/ui/parts";
import { MultiSelectModal } from "@/components/ui/parts/multiSelectModal";
import type { Sex } from "@/gql/__generated__/graphql";
import { useAddressByZipCode } from "@/hooks/zip";
import { errorMessage, i18n, infoMessage } from "@/i18n";
import {
  birthdayValidation,
  buildingValidation,
  cityValidation,
  desiredTimeSlotValidation,
  desiredWithinRangeValidation,
  emailValidation,
  experienceYearsValidation,
  firstNameKanaValidation,
  firstNameValidation,
  imageValidation,
  isExperienceWorkMissing,
  JAPAN_NATIONALITY,
  lastNameKanaValidation,
  lastNameValidation,
  motivationOfJobChangeValidation,
  nationalityTypeValidation,
  nationalityValidation,
  occupationsValidation,
  prefectureValidation,
  sexValidation,
  streetValidation,
  zipCodeValidation,
} from "@/types/validation";
import { showNotification } from "@/util/userNotifications";
import {
  businessGenresQuery,
  caregiverExperienceJobCategoriesQuery,
  identityVerificationStatusQuery,
  occupationQuery,
  updateProfileMutation,
} from "./_graphql";
import {
  desiredTimeSlot,
  desiredWithinRange,
  type EmploymentPeriod,
  employmentPeriod,
  motivationOfJobChange,
} from "./_type";
import { IdentityResubmissionConfirmModal } from "./IdentityResubmissionConfirmModal";
import { NationalityConfirmModal } from "./NationalityConfirmModal";
import { NationalityField } from "./NationalityField";
import { UserImageFields } from "./UserImageFields";
import {
  buildIdentityVerifiedSavedValues,
  useIdentityResubmissionConfirm,
} from "./useIdentityResubmissionConfirm";
import { useNationalityConfirm } from "./useNationalityConfirm";

const schema = z
  .object({
    image: imageValidation(),
    defaultImage: z.string(),
    firstName: firstNameValidation(),
    lastName: lastNameValidation(),
    firstNameKana: firstNameKanaValidation(),
    lastNameKana: lastNameKanaValidation(),
    birthday: birthdayValidation(),
    sex: sexValidation(),
    nationality: nationalityValidation(),
    nationalityType: nationalityTypeValidation(),
    occupation: z.union([occupationsValidation(), z.undefined()]),
    zipCode: zipCodeValidation(),
    prefecture: prefectureValidation(),
    city: cityValidation(),
    street: streetValidation(),
    building: z.union([buildingValidation(), z.undefined()]),
    experienceYears: z.union([experienceYearsValidation(), z.undefined()]),
    experienceWork: z.union([z.string().array(), z.undefined()]),
    experienceFacilities: z.union([z.string().array(), z.undefined()]),
    email: emailValidation(),
    desiredWithinRange: desiredWithinRangeValidation(),
    desiredTimeSlot: desiredTimeSlotValidation(),
    motivationOfJobChange: motivationOfJobChangeValidation(),
  })
  // 経験年数だけを選んで保存すると、サーバー側で介護経験が保存されないためエラーになる。
  // 送信前に弾いて、どの項目を直せばよいかを画面に出す
  .superRefine((data, ctx) => {
    if (!isExperienceWorkMissing(data)) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["experienceWork"],
      message: errorMessage("experienceWorkRequired"),
    });
  });

type Schema = z.input<typeof schema>;

export function UserProfileForm({
  profile,
  address,
  caregiverExperience,
  destinationPath,
  isStepRegistration = false,
}: {
  profile?: {
    id: string;
    authenticated: boolean;
    birthday: string;
    firstName: string;
    imageId: string | null;
    imageUrl: string | null;
    lastName: string;
    firstNameKana: string | null;
    lastNameKana: string | null;
    sex: Sex;
    nationality: string;
    occupation: {
      id: string;
      name: string;
    };
    email: string;
    desiredWithinRange: string | null;
    desiredTimeSlot: string | null;
    motivationOfJobChange: string | null;
  } | null;
  address?: {
    postalCode: string;
    prefecture: string;
    city: string;
    street: string;
    building: string | null;
  };
  caregiverExperience?: {
    id: string;
    businessGenres: {
      id: string;
      name: string;
    }[];
    jobCategories: {
      id: string;
      name: string;
    }[];
    employmentPeriod: string | null;
  } | null;
  destinationPath: string;
  isStepRegistration?: boolean;
}) {
  const {
    control,
    watch,
    getValues,
    setValue,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Schema>({
    mode: "onSubmit",
    defaultValues: {
      firstName: profile?.firstName,
      lastName: profile?.lastName,
      firstNameKana: profile?.firstNameKana ?? "",
      lastNameKana: profile?.lastNameKana ?? "",
      image: null,
      defaultImage: profile?.imageUrl ? profile.imageUrl : "",
      sex: profile?.sex,
      nationality: profile?.nationality ?? JAPAN_NATIONALITY,
      nationalityType:
        profile?.nationality && profile.nationality !== JAPAN_NATIONALITY
          ? "foreign"
          : "japan",
      zipCode: address?.postalCode,
      prefecture: address?.prefecture,
      city: address?.city,
      street: address?.street,
      building: address?.building ?? "",
      occupation: profile?.occupation.id,
      birthday: profile ? new Date(profile.birthday) : undefined,
      experienceYears:
        caregiverExperience?.employmentPeriod as EmploymentPeriod,
      experienceWork: caregiverExperience?.businessGenres.map((j) => j.id),
      experienceFacilities: caregiverExperience?.jobCategories.map((j) => j.id),
      email: profile?.email ?? "",
      desiredWithinRange: profile?.desiredWithinRange || "",
      desiredTimeSlot: profile?.desiredTimeSlot || "",
      motivationOfJobChange: profile?.motivationOfJobChange || "",
    },
    resolver: zodResolver(schema),
  });
  const {
    data: { occupations },
  } = useSuspenseQuery(occupationQuery, { fetchPolicy: "cache-first" });
  const occupationItem = occupations.map((occupation) => {
    return { value: occupation.id, label: occupation.name };
  });
  const {
    data: { businessGenres },
  } = useSuspenseQuery(businessGenresQuery, { fetchPolicy: "cache-first" });
  const jobCategoryItem = businessGenres.map((businessGenre) => {
    return { value: businessGenre.id, label: businessGenre.name };
  });
  const {
    data: { caregiverExperienceJobCategories },
  } = useSuspenseQuery(caregiverExperienceJobCategoriesQuery, {
    fetchPolicy: "cache-first",
  });
  const experienceFacilityItem = caregiverExperienceJobCategories.map(
    (jobCategory) => {
      return { value: jobCategory.id, label: jobCategory.name };
    },
  );
  const [mutation, { loading }] = useMutation(updateProfileMutation, {
    refetchQueries: ["MyPage", "Profile", "Viewer", "RegistrationSteps"],
  });
  const { getAddress, loading: adressLoading } = useAddressByZipCode();
  const router = useRouter();
  const handleGetAdd = async () => {
    const zipcode = getValues("zipCode");
    if (!zipcode) return;
    const { address } = await getAddress(zipcode);
    setValue("prefecture", address.prefecture);
    setValue("city", address.city);
    setValue("street", address.street);
  };
  const defaultImage = watch("defaultImage");
  const apolloClient = useApolloClient();
  const fetchIsIdentityVerified = async () => {
    try {
      const { data } = await apolloClient.query({
        query: identityVerificationStatusQuery,
        fetchPolicy: "network-only",
      });
      return data.profile?.authenticated ?? false;
    } catch {
      // 取り直せなかったときは、画面を開いた時点の状態で判断する
      return profile?.authenticated ?? false;
    }
  };

  const saveProfile = async (
    data: Schema,
    { requiresResubmission }: { requiresResubmission: boolean },
  ) => {
    // 新しい画像がある場合はそれを使用、なければnullを渡す（サーバー側で既存画像を維持）
    const imageToSubmit = data.image !== null ? (data.image as string) : null;

    await mutation({
      variables: {
        input: {
          image: imageToSubmit,
          birthday: formatISO(data.birthday as Date, {
            representation: "complete",
          }),
          firstName: data.firstName ?? "",
          lastName: data.lastName ?? "",
          firstNameKana: data.firstNameKana ?? "",
          lastNameKana: data.lastNameKana ?? "",
          sex: data.sex ?? "male",
          nationality: data.nationality,
          occupationId: data.occupation,
          postalCode: data.zipCode,
          prefecture: data.prefecture,
          city: data.city,
          street: data.street,
          building: data.building,
          businessGenreIds: data.experienceWork,
          jobCategoryIds: data.experienceFacilities ?? [],
          employmentPeriod: data.experienceYears,
          email: data.email,
          desiredWithinRange: data.desiredWithinRange || "",
          desiredTimeSlot: data.desiredTimeSlot || "",
          motivationOfJobChange: data.motivationOfJobChange || "",
        },
      },
      onError: (error) =>
        showNotification({
          type: "error",
          message: error.message,
        }),
      onCompleted: (data) => {
        const res = data.updateResumeProfile;
        if (!res) return;
        showNotification({
          type: "info",
          message: infoMessage("addProfile"),
        });
        if (requiresResubmission) {
          showNotification({
            type: "caution",
            message: infoMessage("identityResubmissionRequired"),
          });
        }
        router.push(destinationPath);
      },
    });
  };
  // 本人確認書類と照合した項目を変更すると本人確認済みが外れるため、保存前に確認を挟む
  const identityResubmissionConfirm = useIdentityResubmissionConfirm(
    saveProfile,
    {
      fetchIsIdentityVerified,
      savedValues: buildIdentityVerifiedSavedValues(profile, address),
    },
  );
  // 国籍の確認を先に出し、国籍を確かめたうえで本人確認書類の再提出の確認に進む
  const nationalityConfirm = useNationalityConfirm(
    identityResubmissionConfirm.submit,
  );

  const onSubmit = async (data: Schema) => {
    // 画像が新規アップロードされていない（null）かつdefaultImageもない場合のみエラー
    // 確認ダイアログより前に出す（従来どおり、写真の不足を先に知らせる）
    if (data.image === null && !data.defaultImage) {
      setError(
        "image",
        {
          type: "required",
          message: "画像をアップロードしてください。",
        },
        { shouldFocus: true },
      );
      return;
    }

    await nationalityConfirm.submit(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack>
        <Paper>
          <UserImageFields
            control={control}
            name="image"
            errors={errors}
            defaultImage={defaultImage}
          />
        </Paper>
        <Paper>
          <Stack>
            <H2>プロフィール詳細</H2>
            <Text>
              <span style={{ color: "red" }}>*</span>
              は求人応募する際に必須事項となります。
            </Text>
            <Text c="dimmed">
              ※
              氏名・住所・生年月日は、本人確認書類の審査で一致を確認本人確認書類と一致する氏名・住所・生年月日をご入力ください。
            </Text>
            <Stack gap="xl">
              <div>
                <Input.Label required>お名前</Input.Label>
                <Group align="flex-end">
                  <InputField
                    label={i18n("lastName")}
                    maw={200}
                    name="lastName"
                    control={control}
                    placeholder="例：山田"
                  />
                  <InputField
                    label={i18n("firstName")}
                    maw={200}
                    name="firstName"
                    control={control}
                    placeholder="例：太郎"
                  />
                </Group>
                <Group>
                  <InputField
                    label={i18n("lastNameKana")}
                    maw={200}
                    name="lastNameKana"
                    control={control}
                    placeholder="例：ヤマダ"
                  />
                  <InputField
                    label={i18n("firstNameKana")}
                    maw={200}
                    name="firstNameKana"
                    control={control}
                    placeholder="例：タロウ"
                  />
                </Group>
              </div>
              <div>
                <Input.Label required>{i18n("birthday")}</Input.Label>
                <DatePickerInputField
                  maw={150}
                  name="birthday"
                  control={control}
                  defaultValue={new Date(2000, 0, 1)}
                />
              </div>
              <RadioField
                withAsterisk
                color="teal"
                label={i18n("sex")}
                name="sex"
                data={[
                  { value: "male", label: "男性" },
                  { value: "female", label: "女性" },
                ]}
                control={control}
              />
              <NationalityField
                control={control}
                name="nationality"
                typeName="nationalityType"
              />
              <InputField
                maw={400}
                label={i18n("email")}
                name="email"
                description=""
                control={control}
                withAsterisk
                placeholder="例：yamada.taro@sample.com"
              />
              <Group align="flex-end">
                <InputField
                  maw={200}
                  label={i18n("zipCode")}
                  name="zipCode"
                  description="※ 半角英数字でハイフンなしで入力。例：2600855"
                  control={control}
                  withAsterisk
                  placeholder="例：2600855"
                />
                <Button
                  color="teal"
                  loading={adressLoading}
                  size="sm"
                  onClick={handleGetAdd}
                >
                  自動入力
                </Button>
              </Group>
              <div>
                <Input.Label required>住所</Input.Label>
                <Group>
                  <InputField
                    label={i18n("prefecture")}
                    name="prefecture"
                    control={control}
                    withAsterisk
                    placeholder="例：千葉県"
                    description="例：千葉県"
                  />
                  <InputField
                    label={i18n("city")}
                    name="city"
                    control={control}
                    withAsterisk
                    placeholder="例：千葉市"
                    description="例：千葉市"
                  />
                </Group>
                <InputField
                  maw={400}
                  label={i18n("street")}
                  name="street"
                  control={control}
                  withAsterisk
                  placeholder="例：中央区市場町１−１"
                  description="例：中央区市場町１−１"
                />
                <InputField
                  maw={400}
                  label={i18n("building")}
                  name="building"
                  control={control}
                  placeholder="例：ケアリンクビル３階"
                  description="例：ケアリンクビル３階"
                />
              </div>
              <MultiSelectModal
                withAsterisk
                name="occupation"
                label={i18n("occupation")}
                type="radio"
                watch={watch}
                maw={250}
                data={occupationItem}
                control={control}
                error={errors.occupation?.message}
              />
              <Divider />

              <Text fw="bold">介護職の経験</Text>
              <MultiSelectModal
                maw={250}
                name="experienceYears"
                label={i18n("experienceYears")}
                type="radio"
                watch={watch}
                data={employmentPeriod}
                control={control}
                error={errors.experienceYears?.message}
              />
              <MultiSelectModal
                name="experienceWork"
                label={i18n("experienceWork")}
                type="checkbox"
                watch={watch}
                data={jobCategoryItem}
                control={control}
                error={errors.experienceWork?.message}
              />
              <MultiSelectModal
                name="experienceFacilities"
                label={i18n("experienceFacilities")}
                type="checkbox"
                watch={watch}
                data={experienceFacilityItem}
                control={control}
                error={errors.experienceFacilities?.message}
              />
              <Divider />
              <Text fw="bold">働きたい希望、転職意欲</Text>
              <MultiSelectModal
                name="desiredWithinRange"
                label={i18n("desiredWithinRange")}
                type="radio"
                watch={watch}
                data={desiredWithinRange}
                control={control}
                error={errors.desiredWithinRange?.message}
              />
              <MultiSelectModal
                name="desiredTimeSlot"
                label={i18n("desiredTimeSlot")}
                type="radio"
                watch={watch}
                data={desiredTimeSlot}
                control={control}
                error={errors.desiredTimeSlot?.message}
              />
              <MultiSelectModal
                name="motivationOfJobChange"
                label={i18n("motivationOfJobChange")}
                type="radio"
                watch={watch}
                data={motivationOfJobChange}
                control={control}
                error={errors.motivationOfJobChange?.message}
              />
              <Center>
                <Button
                  color="teal"
                  size="md"
                  type="submit"
                  loading={
                    loading ||
                    isSubmitting ||
                    identityResubmissionConfirm.checking
                  }
                >
                  {isStepRegistration ? "登録して次へ" : "プロフィールを保存"}
                </Button>
              </Center>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <NationalityConfirmModal
        opened={nationalityConfirm.opened}
        onConfirm={() => void nationalityConfirm.confirm()}
        onCancel={nationalityConfirm.cancel}
      />
      <IdentityResubmissionConfirmModal
        opened={identityResubmissionConfirm.opened}
        onConfirm={() => void identityResubmissionConfirm.confirm()}
        onCancel={identityResubmissionConfirm.cancel}
      />
    </form>
  );
}
